import { MAX_MOVES } from '../../shared/moves.ts';
import { $, enablePixelArtScaling } from '../common/dom.ts';
import { Toast } from '../common/toast.ts';
import { AppearancePicker } from './appearance-picker.ts';
import { AuthScreen, logout, sessionStore } from './auth-screen.ts';
import { BattleScreen } from './battle-screen.ts';
import { BadgeCeremony } from './badge-ceremony.ts';
import { LootReveal } from './loot-reveal.ts';
import { MoveLearnDialog } from './move-learn-dialog.ts';
import { ChatBox } from './chat-box.ts';
import { CoopChip, InviteDialog, PlayerMenu } from './duel-ui.ts';
import { EvolutionScene } from './evolution-scene.ts';
import { GameClient, type GameUi } from './game-client.ts';
import { HelpPanel } from './help-panel.ts';
import { HudMenu } from './hud-menu.ts';
import { InspectPanel } from './inspect-panel.ts';
import { DialogueBox } from './dialogue-box.ts';
import { InteractionPrompt } from './interaction-prompt.ts';
import { QuestLog } from './quest-log.ts';
import { BagPanel } from './bag-panel.ts';
import { OptionsPanel } from './options-panel.ts';
import { DirectionInput } from './input.ts';
import { PokedexPanel } from './pokedex-panel.ts';
import { TrainerCardPanel } from './trainer-card-panel.ts';
import { AchievementsPanel } from './achievements-panel.ts';
import { ShopPanel } from './shop-panel.ts';
import { SoundBoard } from './sound.ts';
import { Soundtrack } from './soundtrack.ts';
import { StarterPicker } from './starter-picker.ts';
import { StoragePanel } from './storage-panel.ts';
import type { OpenMonster } from './monster-card.ts';
import { MonsterSheet } from './monster-sheet.ts';
import { TeamPanel } from './team-panel.ts';
import { WorldMapPanel } from './world-map-panel.ts';
import { WorldRenderer } from './world-renderer.ts';
import { loadGameConfig } from '../common/creature-texts.ts';
import { applyGameTexts } from './game-texts.ts';

enablePixelArtScaling();

// Nom des créatures (configuration du jeu) : chargé avant de construire l'interface, qui le cite partout.
await loadGameConfig('/api/game-config');
applyGameTexts();

/** Écran tactile : croix directionnelle, chat replié, textes adaptés (« touchez »). */
const isTouch = matchMedia('(pointer: coarse)').matches;
document.body.classList.toggle('touch', isTouch);

let client: GameClient | null = null;
const notConnected = () => Promise.reject(new Error('Non connecté.'));

// L'interface est construite une fois ; ses actions sont relayées au client connecté.
const input = new DirectionInput();
if (isTouch) {
  input.bindDpad($('#dpad'));
  bindRunButton($('#btn-run'));
}

/** Bouton B : maintenu, le joueur court ; relâché (ou le doigt glisse hors du bouton), il marche. */
function bindRunButton(button: HTMLElement): void {
  button.hidden = false;
  const hold = (running: boolean) => (event: PointerEvent) => {
    event.preventDefault();
    button.classList.toggle('held', running);
    client?.setRunning(running);
  };
  button.addEventListener('pointerdown', hold(true));
  for (const type of ['pointerup', 'pointercancel', 'pointerleave'] as const) button.addEventListener(type, hold(false));
  button.addEventListener('contextmenu', (event) => event.preventDefault());
}
const sounds = new SoundBoard();
const soundtrack = new Soundtrack(sounds);
const evolution = new EvolutionScene(sounds);
const badgeCeremony = new BadgeCeremony(sounds);
const moveLearn = new MoveLearnDialog({ onLearn: (monsterId, moveIds) => client?.setMonsterMoves(monsterId, moveIds) });
const sheet = new MonsterSheet({
  onGiveItem: (monster) => ui.bag.giveTo(monster, (item) => client?.usePotion(item, monster.id)),
  onRename: (id, nickname) => client?.sendTeamAction({ action: 'rename', id, nickname }),
  onFollow: (monsterId) => client?.setFollower(monsterId),
  onSetMoves: (monsterId, moveIds) => client?.setMonsterMoves(monsterId, moveIds),
});
const inspect = new InspectPanel();
const trainerCard = new TrainerCardPanel((playerId) => client?.fetchTrainerCard(playerId) ?? notConnected());
const openSheet: OpenMonster = (monster, actions, browse) => sheet.show(monster, actions, browse);

const renderer = new WorldRenderer($<HTMLCanvasElement>('#world'));
const ui: GameUi = {
  renderer,
  options: new OptionsPanel(renderer, sounds),
  battle: new BattleScreen({
    onAction: (action) => client?.sendBattleAction(action),
    // Après le combat : les évolutions d'abord (l'une après l'autre), puis la remise du badge, puis les attaques à apprendre.
    onClosed: (evolutions, badge, newMoves) => {
      soundtrack.endBattle();
      const learn = () => {
        // De retour sur la carte : les quêtes et succès débloqués par le combat s'annoncent maintenant.
        client?.releaseBattleNotices();
        moveLearn.offer(newMoves);
      };
      const celebrate = () => {
        if (badge) badgeCeremony.play(badge, learn);
        else learn();
      };
      evolution.playAll(evolutions, celebrate);
    },
  }, sounds),
  evolution,
  badgeCeremony,
  moveLearn,
  lootReveal: new LootReveal($('#game')),
  team: new TeamPanel({
    onAction: (action) => client?.sendTeamAction(action),
    onHeal: (item, monsterId) => client?.usePotion(item, monsterId),
    onOpen: openSheet,
  }),
  chat: new ChatBox((text) => client?.sendChat(text)),
  toast: new Toast($('#toast')),
  pokedex: new PokedexPanel(() => client?.fetchPokedex() ?? notConnected()),
  shop: new ShopPanel((item, quantity) => client?.buy(item, quantity)),
  achievements: new AchievementsPanel(() => client?.fetchAchievements() ?? notConnected()),
  help: new HelpPanel(),
  worldMap: new WorldMapPanel(() => client?.fetchWorldMap() ?? notConnected(), (mapId) => client?.fly(mapId)),
  prompt: new InteractionPrompt(() => client?.interact(), sounds, { persistent: isTouch }),
  dialogue: new DialogueBox((id, choice) => client?.replyToDialogue(id, choice), sounds),
  questLog: new QuestLog(() => client?.fetchQuests() ?? notConnected()),
  bag: new BagPanel((item, equipped) => client?.equip(item, equipped), (item) => client?.useFieldItem(item), (monster, moveId) => {
    // CT : une place libre, l'attaque s'ajoute ; sinon le joueur choisit celle à oublier.
    if (monster.moves.length < MAX_MOVES) return client?.setMonsterMoves(monster.id, [...monster.moves, moveId]);
    ui.bag.close();
    moveLearn.offer([{ monsterId: monster.id, moveId }]);
  }),
  playerMenu: new PlayerMenu(
    (playerId) => client?.challenge(playerId),
    (player) => void inspect.show(player.username, () => client?.inspectPlayer(player.id) ?? notConnected()),
    (player) => void trainerCard.show(player),
    (playerId) => client?.inviteCoop(playerId),
  ),
  inspect,
  trainerCard,
  invite: new InviteDialog((fromId, accept) => client?.respondToDuel(fromId, accept)),
  coopInvite: new InviteDialog((fromId, accept) => client?.respondToCoop(fromId, accept), '#coop-invite'),
  coop: new CoopChip(() => client?.leaveCoop()),
  starter: new StarterPicker((speciesId) => client?.pickStarter(speciesId), sounds),
  storage: new StoragePanel((action) => client?.sendTeamAction(action), openSheet),
  sheet,
  appearance: new AppearancePicker((characterId, outfit) => client?.setCharacter(characterId, outfit)),
  sounds,
  soundtrack,
};
const auth = new AuthScreen(start);

new HudMenu();


function start(token: string): void {
  client?.stop();
  auth.hide();
  client = new GameClient(token, ui, input, {
    onSessionLost(message) {
      client?.stop();
      client = null;
      if (!message) sessionStore.clear();
      auth.show(message);
    },
  });
}

$('#btn-admin').addEventListener('click', () => window.open('/admin', '_blank'));

$('#btn-logout').addEventListener('click', async () => {
  const token = sessionStore.get();
  client?.stop();
  if (token) await logout(token);
  location.reload();
});

const saved = sessionStore.get();
if (saved) start(saved); else auth.show();
